/**
 * SPEYBOOKS | Main Application Router
 * ============================================================================
 * @file      App.tsx (Router)
 * @version   5.7.0 (Migration Mode Update)
 * @context   Primary navigation hub managing Auth, Admin, and Plan-based routing.
 * * ─── TABLE OF CONTENTS ──────────────────────────────────────────────────────
 * * 1. ROUTE GUARDS (Auth & Permissions)
 * - ProtectedRoute : Global auth check; redirects to /login.
 * - AdminRoute     : Validates isAdmin status; uses AdminLayout.
 * - LtdRoute       : Restricts routes to 'limited_company' plan users.
 * * 2. TOP-LEVEL DOMAINS
 * - /login, /register ... : Public-facing authentication pages.
 * - /legal, /privacy ...  : Static legal and information pages.
 * - /admin                : System-wide administration and health checks.
 * - /migration            : TMADD 7.2 Migration Wizard (Isolated Layout).
 * * 3. CORE APPLICATION ROUTES (Layout Wrapped)
 * - /dashboard            : Default landing for authenticated users.
 * - /invoices, /quotes    : Sales and document management.
 * - /contacts, /accounts  : CRM and financial account management.
 * - /transactions         : Banking, imports, and ledger rules.
 * * 4. NESTED SETTINGS (SettingsLayout)
 * - /settings             : User Profile & Security.
 * - /settings/organisation: VAT, defaults, and company details.
 * - /settings/developer   : API & Webhooks.
 * - /settings/billing     : Subscription & Payments.
 * * ─── RECENT CHANGES ─────────────────────────────────────────────────────────
 * v5.7.0: Introduced MigrationLayout for isolated data onboarding.
 * v2.7.0: Refactored Settings into a nested layout structure.
 * ============================================================================
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import Layout from '@/components/Layout';
import AdminLayout from '@/components/AdminLayout';
import SettingsLayout from '@/components/SettingsLayout';
import MigrationLayout from '@/components/MigrationLayout';

// Settings Pages (v2.7.0)

// Admin pages

import toast from 'react-hot-toast';

// ── Lazy-loaded pages ──────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const AccessibilityPage = lazy(() => import('@/pages/AccessibilityPage'));
const SitemapPage = lazy(() => import('@/pages/SitemapPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const InvoiceCreatePage = lazy(() => import('@/pages/InvoiceCreatePage'));
const InvoiceDetailPage = lazy(() => import('@/pages/InvoiceDetailPage'));
const QuotesPage = lazy(() => import('@/pages/QuotesPage'));
const QuoteCreatePage = lazy(() => import('@/pages/QuoteCreatePage'));
const QuoteDetailPage = lazy(() => import('@/pages/QuoteDetailPage'));
const ContactsPage = lazy(() => import('@/pages/ContactsPage'));
const ContactCreatePage = lazy(() => import('@/pages/ContactCreatePage'));
const ContactDetailPage = lazy(() => import('@/pages/ContactDetailPage'));
const DirectorsPage = lazy(() => import('@/pages/DirectorsPage'));
const DirectorCreatePage = lazy(() => import('@/pages/DirectorCreatePage'));
const DirectorDetailPage = lazy(() => import('@/pages/DirectorDetailPage'));
const DividendWizardPage = lazy(() => import('@/pages/DividendWizardPage'));
const DividendSummaryPage = lazy(() => import('@/pages/DividendSummaryPage'));
const BoardMinutesPage = lazy(() => import('@/pages/BoardMinutesPage'));
const AccountsPage = lazy(() => import('@/pages/AccountsPage'));
const AccountCreatePage = lazy(() => import('@/pages/AccountCreatePage'));
const AccountDetailPage = lazy(() => import('@/pages/AccountDetailPage'));
const TransactionsPage = lazy(() => import('@/pages/TransactionsPage'));
const TransactionCreatePage = lazy(() => import('@/pages/TransactionCreatePage'));
const TransactionDetailPage = lazy(() => import('@/pages/TransactionDetailPage'));
const BankImportPage = lazy(() => import('@/pages/BankImportPage'));
const CategorisationRulesPage = lazy(() => import('@/pages/CategorisationRulesPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const LogsPage = lazy(() => import('@/pages/LogsPage'));
const GeneralSettingsPage = lazy(() => import('@/pages/settings/GeneralSettingsPage'));
const OrganisationSettingsPage = lazy(() => import('@/pages/settings/OrganisationSettingsPage'));
const DeveloperSettingsPage = lazy(() => import('@/pages/settings/DeveloperSettingsPage'));
const BillingSettingsPage = lazy(() => import('@/pages/settings/BillingSettingsPage'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('@/pages/admin/AdminUserDetailPage'));
const AdminOrgsPage = lazy(() => import('@/pages/admin/AdminOrgsPage'));
const AdminOrgDetailPage = lazy(() => import('@/pages/admin/AdminOrgDetailPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/AdminAuditLogPage'));
const AdminHealthPage = lazy(() => import('@/pages/admin/AdminHealthPage'));
const AdminBugReportsPage = lazy(() => import('@/pages/admin/AdminBugReportsPage'));
const ContactImportPage = lazy(() => import('@/pages/ContactImportPage'));
const OpeningBalancesPage = lazy(() => import('@/pages/OpeningBalancesPage'));
const InvoiceImportPage = lazy(() => import('@/pages/InvoiceImportPage'));
const BillImportPage = lazy(() => import('@/pages/BillImportPage'));
const MigrationWizardPage = lazy(() => import('@/pages/MigrationWizardPage'));

// Loading fallback for lazy routes
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heather-600" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heather-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isAdmin) {
    toast.error('Access denied. Admin privileges required.');
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * LtdRoute — pathless layout route guard for limited company features.
 *
 * Renders <Outlet /> (passthrough) for ltd users, redirects sole traders
 * to dashboard. Future Ltd-only features just nest their routes inside this guard.
 */
function LtdRoute() {
  const { user } = useAuth();

  if (user?.organisation?.plan !== 'limited_company') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
      <Route path="/register" element={<Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />
      <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>} />
      <Route path="/verify-email" element={<Suspense fallback={<PageLoader />}><VerifyEmailPage /></Suspense>} />

      {/* Public legal/info pages */}
      <Route path="/legal" element={<Suspense fallback={<PageLoader />}><LegalPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
      <Route path="/cookies" element={<Suspense fallback={<PageLoader />}><CookiesPage /></Suspense>} />
      <Route path="/accessibility" element={<Suspense fallback={<PageLoader />}><AccessibilityPage /></Suspense>} />
      <Route path="/sitemap" element={<Suspense fallback={<PageLoader />}><SitemapPage /></Suspense>} />

      {/* Admin Panel */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUsersPage /></Suspense>} />
        <Route path="users/:id" element={<Suspense fallback={<PageLoader />}><AdminUserDetailPage /></Suspense>} />
        <Route path="orgs" element={<Suspense fallback={<PageLoader />}><AdminOrgsPage /></Suspense>} />
        <Route path="orgs/:id" element={<Suspense fallback={<PageLoader />}><AdminOrgDetailPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoader />}><AdminSettingsPage /></Suspense>} />
        <Route path="audit-log" element={<Suspense fallback={<PageLoader />}><AdminAuditLogPage /></Suspense>} />
        <Route path="health" element={<Suspense fallback={<PageLoader />}><AdminHealthPage /></Suspense>} />
        <Route path="bug-reports" element={<Suspense fallback={<PageLoader />}><AdminBugReportsPage /></Suspense>} />
      </Route>

      {/* Migration Mode — dedicated layout, no app chrome (TMADD 7.2) */}
      <Route
        path="/migration"
        element={
          <ProtectedRoute>
            <MigrationLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<PageLoader />}><MigrationWizardPage /></Suspense>} />
      </Route>

      {/* Main Application */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
        <Route path="invoices" element={<Suspense fallback={<PageLoader />}><InvoicesPage /></Suspense>} />
        <Route path="invoices/new" element={<Suspense fallback={<PageLoader />}><InvoiceCreatePage /></Suspense>} />
        <Route path="invoices/:id" element={<Suspense fallback={<PageLoader />}><InvoiceDetailPage /></Suspense>} />
        <Route path="quotes" element={<Suspense fallback={<PageLoader />}><QuotesPage /></Suspense>} />
        <Route path="quotes/new" element={<Suspense fallback={<PageLoader />}><QuoteCreatePage /></Suspense>} />
        <Route path="quotes/:id" element={<Suspense fallback={<PageLoader />}><QuoteDetailPage /></Suspense>} />
        <Route path="contacts" element={<Suspense fallback={<PageLoader />}><ContactsPage /></Suspense>} />
        <Route path="contacts/new" element={<Suspense fallback={<PageLoader />}><ContactCreatePage /></Suspense>} />
        <Route path="contacts/:id" element={<Suspense fallback={<PageLoader />}><ContactDetailPage /></Suspense>} />
        <Route path="contacts/import" element={<Suspense fallback={<PageLoader />}><ContactImportPage /></Suspense>} />
        <Route path="opening-balances" element={<Suspense fallback={<PageLoader />}><OpeningBalancesPage /></Suspense>} />
        <Route path="invoices/import" element={<Suspense fallback={<PageLoader />}><InvoiceImportPage /></Suspense>} />
        <Route path="bills/import" element={<Suspense fallback={<PageLoader />}><BillImportPage /></Suspense>} />

        {/* Ltd company features — guarded by LtdRoute */}
        <Route element={<LtdRoute />}>
          <Route path="directors" element={<Suspense fallback={<PageLoader />}><DirectorsPage /></Suspense>} />
          <Route path="directors/new" element={<Suspense fallback={<PageLoader />}><DirectorCreatePage /></Suspense>} />
          <Route path="directors/:id" element={<Suspense fallback={<PageLoader />}><DirectorDetailPage /></Suspense>} />
          <Route path="dividends/summary" element={<Suspense fallback={<PageLoader />}><DividendSummaryPage /></Suspense>} />
          <Route path="dividends/board-minutes" element={<Suspense fallback={<PageLoader />}><BoardMinutesPage /></Suspense>} />
          <Route path="dividends/new" element={<Suspense fallback={<PageLoader />}><DividendWizardPage /></Suspense>} />
        </Route>

        <Route path="accounts" element={<Suspense fallback={<PageLoader />}><AccountsPage /></Suspense>} />
        <Route path="accounts/new" element={<Suspense fallback={<PageLoader />}><AccountCreatePage /></Suspense>} />
        <Route path="accounts/:id" element={<Suspense fallback={<PageLoader />}><AccountDetailPage /></Suspense>} />
        <Route path="transactions" element={<Suspense fallback={<PageLoader />}><TransactionsPage /></Suspense>} />
        <Route path="transactions/new" element={<Suspense fallback={<PageLoader />}><TransactionCreatePage /></Suspense>} />
        <Route path="transactions/import" element={<Suspense fallback={<PageLoader />}><BankImportPage /></Suspense>} />
        <Route path="transactions/:id" element={<Suspense fallback={<PageLoader />}><TransactionDetailPage /></Suspense>} />
        <Route path="rules" element={<Suspense fallback={<PageLoader />}><CategorisationRulesPage /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<PageLoader />}><ReportsPage /></Suspense>} />
        <Route path="logs" element={<Suspense fallback={<PageLoader />}><LogsPage /></Suspense>} />

        {/* Settings with nested routes */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Suspense fallback={<PageLoader />}><GeneralSettingsPage /></Suspense>} />
          <Route path="organisation" element={<Suspense fallback={<PageLoader />}><OrganisationSettingsPage /></Suspense>} />
          <Route path="developer" element={<Suspense fallback={<PageLoader />}><DeveloperSettingsPage /></Suspense>} />
          <Route path="billing" element={<Suspense fallback={<PageLoader />}><BillingSettingsPage /></Suspense>} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
